import axios from "axios";
import { useAuth } from "./auth.service";
import { useMemo } from "react";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

export const useMessageRedirectRulesService = () => {
  const { user } = useAuth();
  const token = user?.accessToken || user?.token;

  return useMemo(() => {
    const getAuthHeaders = () => {
      return {
        "Content-Type": "application/json",
        "x-access-token": token,
        "Authorization": token ? `Bearer ${token}` : undefined,
      };
    };

    /**
     * Obtiene la lista de reglas de redirección con filtros opcionales
     * @param {Object} queryParams - { search, isActive, limit, skip }
     */
    const getRules = async (queryParams = {}) => {
      try {
        const params = new URLSearchParams();
        if (queryParams.search !== undefined && queryParams.search !== null && queryParams.search !== "") {
          params.append("search", queryParams.search);
        }
        if (queryParams.isActive !== undefined && queryParams.isActive !== null && queryParams.isActive !== "") {
          params.append("isActive", queryParams.isActive);
        }
        if (queryParams.limit !== undefined && queryParams.limit !== null) {
          params.append("limit", queryParams.limit.toString());
        }
        if (queryParams.skip !== undefined && queryParams.skip !== null) {
          params.append("skip", queryParams.skip.toString());
        }

        const queryString = params.toString();
        const url = `${API_BASE_URL}/messages/redirect-rules${queryString ? `?${queryString}` : ""}`;

        const response = await axios.get(url, { headers: getAuthHeaders() });
        return response.data;
      } catch (error) {
        console.error("Error al obtener reglas de redirección:", error);
        throw error;
      }
    };

    /**
     * Obtiene los detalles de una regla específica por ID
     * @param {string} id
     */
    const getRule = async (id) => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/messages/redirect-rules/${id}`,
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (error) {
        console.error(`Error al obtener regla ${id}:`, error);
        throw error;
      }
    };

    /**
     * Crea una nueva regla de redirección
     * @param {Object} ruleData
     */
    const createRule = async (ruleData) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/messages/redirect-rules`,
          ruleData,
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (error) {
        console.error("Error al crear regla de redirección:", error);
        throw error;
      }
    };

    /**
     * Actualiza una regla existente
     * @param {string} id
     * @param {Object} ruleData
     */
    const updateRule = async (id, ruleData) => {
      try {
        const response = await axios.put(
          `${API_BASE_URL}/messages/redirect-rules/${id}`,
          ruleData,
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (error) {
        console.error(`Error al actualizar regla ${id}:`, error);
        throw error;
      }
    };

    /**
     * Elimina una regla por ID
     * @param {string} id
     */
    const deleteRule = async (id) => {
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/messages/redirect-rules/${id}`,
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (error) {
        console.error(`Error al eliminar regla ${id}:`, error);
        throw error;
      }
    };

    /**
     * Activa o desactiva una regla
     * @param {string} id
     */
    const toggleRule = async (id) => {
      try {
        const response = await axios.patch(
          `${API_BASE_URL}/messages/redirect-rules/${id}/toggle`,
          {},
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (error) {
        console.error(`Error al alternar estado de la regla ${id}:`, error);
        throw error;
      }
    };

    /**
     * Prueba en tiempo real la conectividad hacia targetUrl
     * @param {Object} ruleData - { targetUrl, httpMethod, timeoutMs, headers }
     */
    const testRuleConnection = async (ruleData) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/messages/redirect-rules/test`,
          { ruleData },
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (error) {
        console.error("Error al probar conexión de webhook:", error);
        throw error;
      }
    };

    return {
      getRules,
      getRule,
      createRule,
      updateRule,
      deleteRule,
      toggleRule,
      testRuleConnection,
    };
  }, [token]);
};
